import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAirtable } from '../hooks/useAirtable';
import { contactsAPI } from '../services/airtable';
import '../styles/member-detail.css';

const MemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: contacts, loading: contactsLoading } = useAirtable(contactsAPI.getAll, false);

  const [member, setMember] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [hoursPerWeek, setHoursPerWeek] = useState(0);

  useEffect(() => {
    // Try to get member from location state first
    if (location.state?.member) {
      setMember(location.state.member);
      window.scrollTo(0, 0); // Scroll to top
      return;
    }

    // Otherwise fetch from contacts
    if (contacts) {
      const contactsList = Array.isArray(contacts) ? contacts : (contacts?.records || []);
      const foundMember = contactsList.find(c => c.id === id);
      if (foundMember) {
        setMember(foundMember);
        window.scrollTo(0, 0); // Scroll to top
      }
    }
  }, [id, contacts, location.state]);

  useEffect(() => {
    // Log member data structure when member is loaded
    if (member) {
      console.log('📦 MEMBER DATA LOADED:', member);
      console.log('🔑 AVAILABLE FIELDS:', Object.keys(member));
      console.log('📋 FIELD VALUES:');
      Object.keys(member).forEach(key => {
        console.log(`  ${key}:`, member[key]);
      });
    }
  }, [member]);
  const getAvailabilityColor = (availability) => {
    if (!availability) return '#6366f1';

    const status = availability.toLowerCase().trim();
    if (status === 'available for work') {
      return '#10b981'; // green
    } else if (status === 'full load') {
      return '#ef4444'; // red
    } else if (status === 'part time') {
      return '#f59e0b'; // amber
    }
    return '#6366f1'; // default indigo
  };

  const getAvailabilityLabel = (availability) => {
    if (!availability) return 'Available';

    const status = availability.toLowerCase().trim();
    if (status === 'available for work') {
      return 'Available for work';
    } else if (status === 'full load') {
      return 'Full Load';
    } else if (status === 'part time') {
      return 'Part Time';
    }
    return availability;
  };

  // Get resume URL
  const getResumeUrl = () => {
    if (!member.Resume) return null;
    return Array.isArray(member.Resume) ? member.Resume[0]?.url : member.Resume;
  };

  if (contactsLoading) {
    return (
      <div className="member-detail">
        <div className="loading-skeleton">
          <div>Loading member details...</div>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="member-detail">
        <div className="error-message">
          <h2>Member not found</h2>
          <button onClick={() => navigate('/')} className="back-button">
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  const imageUrl = Array.isArray(member.Photo) ? member.Photo[0]?.url : member.Photo;
  const availabilityColor = getAvailabilityColor(member.Availability);
  const availabilityLabel = getAvailabilityLabel(member.Availability);
  const initials = member.Name ? member.Name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
  const resumeUrl = getResumeUrl();

  return (
    <div className="member-detail">
      <div className="member-detail-container">
        <button className="back-button" onClick={() => navigate('/')}>
          ← Back to Services
        </button>

        <div className="member-detail-content">
          {/* Left Side: Photo and Contact Icons */}
          <div className="member-detail-photo-section">
            <div className="member-detail-photo">
              <div
                className="member-detail-avatar"
                style={{
                  borderColor: availabilityColor,
                  boxShadow: `0 0 0 5px ${availabilityColor}33`
                }}
              >
                {imageUrl ? (
                  <img src={imageUrl} alt={member.Name} />
                ) : (
                  <div className="member-detail-initials">{initials}</div>
                )}
              </div>
              <span
                className="availability-badge"
                style={{ backgroundColor: availabilityColor }}
              >
                {availabilityLabel}
              </span>
            </div>

            {/* Contact Icons */}
            <div className="contact-icons">
              {member.Email && (
                <a
                  href={`mailto:${member.Email}`}
                  className="contact-icon email-icon"
                  title="Send Email"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ✉️
                </a>
              )}
              {member.Linkedin && (
                <a
                  href={member.Linkedin}
                  className="contact-icon linkedin-icon"
                  title="Visit LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  💼
                </a>
              )}
              {member.Website && (
                <a
                  href={member.Website}
                  className="contact-icon website-icon"
                  title="Visit Website"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  🌐
                </a>
              )}
              {member.Whatsapp && (
                <a
                  href={`https://wa.me/${member.Whatsapp}`}
                  className="contact-icon whatsapp-icon"
                  title="Contact on WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  💬
                </a>
              )}
              {resumeUrl && (
                <a
                  href={resumeUrl}
                  className="contact-icon resume-icon"
                  title="Download Resume"
                  download
                >
                  📄
                </a>
              )}
            </div>

            {/* Rate Calculator */}
            {member['Rate per Hour'] && (
              <div className="rate-calculator">
                <h3>Cost Estimator</h3>
                <div className="calculator-inputs">
                  <div className="calculator-input-group">
                    <label htmlFor="hoursInput">Hours needed per week (USD)</label>
                    <input
                      id="hoursInput"
                      type="number"
                      min="0"
                      step="0.5"
                      value={hoursPerWeek}
                      onChange={(e) => setHoursPerWeek(parseFloat(e.target.value) || 0)}
                      placeholder="Enter hours per week"
                      className="hours-input"
                    />
                  </div>
                </div>

                {hoursPerWeek > 0 && (
                  <div className="calculator-results">
                    <div className="result-item">
                      <span className="result-label">Weekly Cost:</span>
                      <span className="result-value">
                        ${(hoursPerWeek * member['Rate per Hour']).toFixed(2)}
                      </span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Monthly Cost (4 weeks):</span>
                      <span className="result-value">
                        ${(hoursPerWeek * member['Rate per Hour'] * 4).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Side: Details */}
          <div className="member-detail-info">
            <div className="member-detail-header">
              <h1 className="member-detail-name">{member.Name}</h1>
              {member.Title && <p className="member-detail-title">{member.Title}</p>}
            </div>

            <div className="member-fields">
              {/* Years of Experience and Rate per Hour side by side */}
              <div className="member-fields-row">
                {member['Years of Exp'] && (
                  <div className="member-field">
                    <label>Years of Experience</label>
                    <p>{member['Years of Exp']}</p>
                  </div>
                )}

                {member['Rate per Hour'] && (
                  <div className="member-field">
                    <label>Rate per Hour</label>
                    <p>${member['Rate per Hour']}/hr</p>
                  </div>
                )}
              </div>

              {member.Address && (
                <div className="member-field">
                  <label>Address</label>
                  <p>{member.Address}</p>
                </div>
              )}

              {member.Story && (
                <div className="member-field">
                  <label>Story</label>
                  <p>{member.Story}</p>
                </div>
              )}

              {member.Tools && (
                <div className="member-field">
                  <label>Tools & Skills</label>
                  <p>{member.Tools}</p>
                </div>
              )}

              {/* Display any other fields that have data */}
              {member.Phone && (
                <div className="member-field">
                  <label>Phone</label>
                  <p>{member.Phone}</p>
                </div>
              )}

              {member.Bio && (
                <div className="member-field">
                  <label>Bio</label>
                  <p>{member.Bio}</p>
                </div>
              )}

              {member.Notes && (
                <div className="member-field">
                  <label>Notes</label>
                  <p>{member.Notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberDetail;
